/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef} from 'react';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import Polyline from '@arcgis/core/geometry/Polyline';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel';
import {loadModules} from 'esri-loader';
import {error} from '@ion/core/src/logger/logger';
import {getUserUnit} from '@ion/core/src/units/helpers';
import Unit from '@ion/core/src/units/units';
import {useKeyPress} from '../../hooks/useKeyPress';
import {dlsColor} from '../../theme/theme';
import {isCurrentTheme} from '../../theme/useThemeContext';
import {BoundaryMode} from '../../types/types';
import {
  getExponentialBuffer,
  getKmInDegree,
  getMetersInDegree,
  getMilesInDegree
} from '../helpers/globeUtils';
import {
  getMapPoints,
  removeActiveFence
} from '../helpers/MapViewUtils';
import {DEFAULT_SYMBOL, DEFAULT_UNIT} from './constants';
import {
  getActivePointSymbol,
  getFinishedPointSymbol,
  registerCurrentDrawingUpdates,
  startGeofenceDrawingSession
} from './drawingStyles';
import {getRadialEdge, VICINITY_INITIAL_RADIUS} from './radialDrawing';

// defines the prop definitions for the CreateRadial
export type CreateRadialProps = {
  map: __esri.Map,
  mapView: __esri.MapView,
  radius?: number;
  radiusFromPanel?: number;
  setRadiusFromMap?: Function;
  setDrawingComplete?: (val: boolean) => void;
  setFinalDrawingDetails?: (val: {rings: Array<any>}) => void;
  finishedDrawing?: boolean;
  isFMTracking?: boolean;
  mapViewEl?: HTMLElement;
  geoFencesGraphicsLayerRef?: {
    current: null | __esri.Layer;
  };
  sketchViewModelRef?: {
    current: null | __esri.SketchViewModel;
  };
  selectedMapPointRef?: {
    current: null | __esri.Layer;
  };
  cancelDrawing?: Function;
  mapClickHandler?: any;
  setMapClickHandler?: (val: any) => void;
  boundaryMode?: BoundaryMode | null;
};

type RadialProps = {
  GraphicsLayer: __esri.GraphicsLayer;
  Graphic: __esri.Graphic;
  SketchViewModel: __esri.SketchViewModel;
  Polyline: __esri.Polyline;
  geometryEngine: __esri.geometryEngine;
};

/**
 * Provides Drawing radial functionality
 *
 * ```typescript
 *
 *    const [radiusValue, setRadiusValue] = useState(100);
 *
 *    <CreateRadial map={map} mapView={mapView} radius={radiusValue} />
 *
 * ```
 *
 */
export const CreateRadial = ({
  map,
  mapView,
  boundaryMode,
  radius: radiusValue,
  radiusFromPanel,
  setRadiusFromMap,
  setDrawingComplete,
  setFinalDrawingDetails,
  finishedDrawing,
  mapViewEl,
  geoFencesGraphicsLayerRef,
  sketchViewModelRef,
  selectedMapPointRef,
  cancelDrawing,
  mapClickHandler,
  setMapClickHandler,
  isFMTracking = false
}: CreateRadialProps) => {
  const deleteKey                  = useKeyPress('Delete');
  const escapeKey                  = useKeyPress('Escape');
  const vicinityDrawInitializedRef = useRef(false);
  const unit                       = getUserUnit();

  useEffect(() => {
    vicinityDrawInitializedRef.current = !(boundaryMode === BoundaryMode.Vicinity);
  }, [boundaryMode]);

  useEffect(() => {
    initializeDrawing();
    // the current theme's drawing session - hovering the radial's center /
    // edge means it can be dragged ...
    return isCurrentTheme() ? startGeofenceDrawingSession(mapViewEl, true) : undefined;
  }, []);

  useEffect(() => {
    if (radiusFromPanel) {
      initializeDrawing();
    }
    return () => {
      mapViewEl?.classList?.remove('default-cursor');
      mapViewEl?.classList?.remove('crosshair');
    };
  }, [radiusValue]);

  useEffect(() => {
    return function() {
      if (geoFencesGraphicsLayerRef.current) {
        sketchViewModelRef.current?.complete();
        sketchViewModelRef.current?.cancel();
        map.remove(geoFencesGraphicsLayerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (deleteKey || escapeKey) {
      if (geoFencesGraphicsLayerRef.current) {
        // If the user is currently manipulating a drawing
        // graphic delete it and re-initialize the drawing tool
        removeActiveFence(
          map,
          [geoFencesGraphicsLayerRef, sketchViewModelRef, selectedMapPointRef]
        );
        cancelDrawing();
        setDrawingComplete(false);
        mapClickHandler?.remove();
      }
    }
  }, [deleteKey, escapeKey]);

  useEffect(() => {
    if (finishedDrawing) {
      const items = geoFencesGraphicsLayerRef.current?.graphics?.items;
      if (items?.length && items[0].geometry?.rings) {
        setFinalDrawingDetails(items[0].geometry.rings[0]);
      }
    };
  }, [finishedDrawing]);

  useEffect(() => {
    if (selectedMapPointRef?.current) {
      initializeDrawing();
      setDrawingComplete(true);
    }
  }, [selectedMapPointRef?.current]);

  function initializeDrawing() {
    if (!isFMTracking) {
      loadModules([
        'esri/layers/GraphicsLayer',
        'esri/Graphic',
        'esri/widgets/Sketch/SketchViewModel',
        'esri/geometry/Polyline',
        'esri/geometry/geometryEngine'
      ])
        .then(
          ([GraphicsLayer, Graphic, SketchViewModel, Polyline, geometryEngine]) => {
            createRadial({GraphicsLayer, Graphic, SketchViewModel, Polyline, geometryEngine});
          }
        )
        .catch((err) => {
          error(err);
        });
    } else {
      createRadial({GraphicsLayer, Graphic, SketchViewModel, Polyline, geometryEngine} as any);
    }
  };

  const createRadial = useCallback(({
    Graphic,
    Polyline,
    GraphicsLayer,
    geometryEngine,
    SketchViewModel
  }: RadialProps) => {
    let centerGraphic,
      edgeGraphic,
      polylineGraphic,
      bufferGraphic,
      centerGeometryAtStart;
    if (selectedMapPointRef?.current) {
      drawBufferPolygon(
        selectedMapPointRef.current.longitude,
        selectedMapPointRef.current.latitude,
        true
      );
    } else {
      const viewClickHandler = mapView.on('immediate-click', function(event) {
        event.stopPropagation();
        if (sketchViewModelRef?.current?.state === 'active') {
          return;
        }
        selectedMapPointRef.current = event.mapPoint;
        drawBufferPolygon(event.mapPoint.longitude, event.mapPoint.latitude, false);
        setDrawingComplete(true);
      });
      setMapClickHandler(viewClickHandler);
      mapViewEl?.classList?.add('crosshair');
      mapViewEl?.classList?.remove('default-cursor');
    }

    function onSetRadiusFromMap(radius) {
      const radiusValue = unit === Unit.METRIC
        ? radius / 1000 // convert meters → km
        : radius / 1609.344; // convert meters → miles

      setRadiusFromMap?.(Math.round(boundaryMode === BoundaryMode.Vicinity ? radiusValue : radius));
    }

    function drawBufferPolygon(mapPointLongitude, mapPointLatitude, editing) {
      const unitDegree        = unit === Unit.METRIC ? getKmInDegree : getMilesInDegree;
      const getDegree         = boundaryMode === BoundaryMode.Vicinity ? unitDegree : getMetersInDegree;
      const degrees           = getDegree(1); // make radial buffer 1 meter/miles/km
      const isCurrent         = isCurrentTheme();
      // the current theme's vicinity search circle starts at 1 mi / km - the
      // zoom based buffer below is in meters, which the vicinity search would
      // otherwise draw as that many miles / kilometers ...
      const isCurrentVicinity = isCurrent && boundaryMode === BoundaryMode.Vicinity;

      // this means that the user is dragging the radius so we dont need to
      // recalculate the radius it's already being called in onMove
      if (editing && !radiusFromPanel && vicinityDrawInitializedRef.current) return;

      vicinityDrawInitializedRef.current = true;

      // if editing is false then the user just placed the initial circle on the map
      // use a calculated radius based on the zoom level and set the radius value
      // as such
      const initialRadius = isCurrentVicinity ? VICINITY_INITIAL_RADIUS : getExponentialBuffer(mapView.zoom);
      const {newLatitude, newLongitude} = getRadialEdge({
        latitude       : mapPointLatitude,
        longitude      : mapPointLongitude,
        radius         : editing ? radiusValue : initialRadius,
        degreesPerUnit : degrees,
        isCurrent
      });
      if (!editing) {
        if (isCurrentVicinity) {
          setRadiusFromMap?.(VICINITY_INITIAL_RADIUS);
        } else {
          const bufferInMeters = getExponentialBuffer(mapView.zoom);
          onSetRadiusFromMap(bufferInMeters);
        }
      };
      const {
        vertices,
        centerPoint,
        edgePoint
      } = getMapPoints({
        mapView,
        mapPointLongitude,
        mapPointLatitude,
        newLatitude,
        newLongitude
      });

      // Create center, edge, polyline and buffer graphics
      const polyline = new Polyline({
        paths            : vertices,
        spatialReference : mapView.spatialReference
      });

      // get the length of the polyline and create buffer
      const length = geometryEngine.geodesicLength(polyline, DEFAULT_UNIT);
      const buffer = geometryEngine.geodesicBuffer(
        centerPoint,
        length,
        DEFAULT_UNIT
      );

      // Create the graphics representing the line and buffer; the current
      // theme marks the center as finished and the edge as the drag handle
      const pointSymbol = isCurrent ? getFinishedPointSymbol() : {
        type  : 'simple-marker',
        style : 'circle',
        size  : 10,
        color : dlsColor('skyBlue')
      };

      centerGraphic = new Graphic({
        geometry   : centerPoint,
        symbol     : pointSymbol,
        attributes : {
          center : 'center'
        }
      });

      edgeGraphic = new Graphic({
        geometry : edgePoint,
        symbol   : isCurrent ? getActivePointSymbol() : {
          type  : 'simple-marker',
          style : 'circle',
          size  : 10,
          color : dlsColor('white')
        },
        attributes : {
          edge : 'edge'
        }
      });

      polylineGraphic = new Graphic({
        geometry : polyline,
        symbol   : {
          type  : 'simple-line',
          color : dlsColor('skyBlue'),
          width : 3
        }
      });

      bufferGraphic = new Graphic({
        geometry : buffer,
        symbol   : DEFAULT_SYMBOL()
      });

      if (geoFencesGraphicsLayerRef.current) {
        sketchViewModelRef.current?.cancel();
        (geoFencesGraphicsLayerRef.current as any).removeAll();
      } else {
        geoFencesGraphicsLayerRef.current = new GraphicsLayer();
        map.layers.add(geoFencesGraphicsLayerRef.current);
        map.layers.reorder(geoFencesGraphicsLayerRef.current, 1);
      }
      // Add graphics to layer
      geoFencesGraphicsLayerRef.current?.addMany([
        bufferGraphic,
        centerGraphic,
        edgeGraphic,
        polylineGraphic
      ]);

      setUpSketch();

      sketchViewModelRef.current?.update([edgeGraphic, centerGraphic], {
        tool : 'move'
      });

      calculateBuffer(vertices);
    }

    /*
     * Edge or center point is being updated. Recalculate the buffer with
     * updated geometry information.
     */
    function calculateBuffer(vertices) {
      polylineGraphic.geometry = new Polyline({
        paths            : vertices,
        spatialReference : mapView.spatialReference
      });

      // Recalculate the polyline length and buffer polygon
      const length = geometryEngine.geodesicLength(
        polylineGraphic.geometry,
        DEFAULT_UNIT
      );

      const buffer = geometryEngine.geodesicBuffer(
        centerGraphic.geometry,
        length,
        DEFAULT_UNIT
      );

      // Update the buffer polygon
      bufferGraphic.geometry = buffer;
    }

    function setUpSketch() {
      sketchViewModelRef.current = new SketchViewModel({
        view                 : mapView,
        layer                : geoFencesGraphicsLayerRef.current,
        updateOnGraphicClick : false,
        defaultUpdateOptions : {
          toggleToolOnClick : false
        }
      });
      mapViewEl?.classList.add('default-cursor');
      mapViewEl?.classList.remove('crosshair');

      // Listen to SketchViewModel's update event
      sketchViewModelRef.current?.on('update', onMove);
      // the current theme drops the move tool's highlight halo ...
      registerCurrentDrawingUpdates(sketchViewModelRef.current, isCurrentTheme());
    }

    /*
     * Edge or center graphics are being moved. Recalculate the buffer with
     * updated geometry information again.
     */
    function onMove(event) {
    // If the edge graphic is moving, keep the center graphic
    // at its initial location. Only move edge graphic
      if (event.toolEventInfo?.mover?.attributes?.edge) {
        const toolType = event.toolEventInfo.type;
        if (toolType === 'move-start') {
          centerGeometryAtStart = centerGraphic.geometry;
        } else if (toolType === 'move' || toolType === 'move-stop') {
          // keep the center graphic at its initial location when edge point is moving
          centerGraphic.geometry = centerGeometryAtStart;
        }
      }

      // the center or edge graphic is being moved, recalculate the buffer
      const vertices = [
        [centerGraphic.geometry.x, centerGraphic.geometry.y],
        [edgeGraphic.geometry.x, edgeGraphic.geometry.y]
      ];
      calculateBuffer(vertices);

      if (event.state === 'complete') {
        sketchViewModelRef.current?.update([edgeGraphic, centerGraphic], {
          tool : 'move'
        });
      } else if (event.state === 'active') {
      // calculate the radius length
        const length = geometryEngine.geodesicLength(
          polylineGraphic.geometry,
          DEFAULT_UNIT
        );
        onSetRadiusFromMap(length);
      }
    }
  }, [selectedMapPointRef?.current, radiusValue, radiusFromPanel]);

  return null;
};

export default CreateRadial;
